import { ChangeDetectionStrategy, Component, computed, input, OnDestroy, output } from '@angular/core';
import { DeclarativeMapBase } from '../declarative-map-base/declarative-map-base.component';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { computedPrevious } from 'ngxtension/computed-previous';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { filter, takeUntil, tap } from 'rxjs';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { MapPane } from '@cue/map/core';

@Component({
  selector: 'cue-declarative-map-layer',
  imports: [],
  templateUrl: './declarative-map-layer.component.html',
  styleUrl: './declarative-map-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeclarativeMapLayerComponent extends DeclarativeMapBase implements OnDestroy {
  fitMap = input(false);
  geoJson = input();
  radius = input<number>();
  color = input('blue');
  fill = input(true);
  fillColor = input('blue');
  fillOpacity = input(0.5);
  interactive = input(false);
  pane = input<MapPane>();
  weight = input(3);

  terminator = injectDestroy();
  click = outputFromObservable(
    this.adapter.layerClicked.pipe(
      takeUntil(this.terminator),
      filter((x) => x === this.identifier)
    )
  );

  appended = outputFromObservable(
    this.adapter.layerAppended.pipe(
      takeUntil(this.terminator),
      filter((x) => x === this.identifier)
    )
  );

  removed = outputFromObservable(
    this.adapter.layerRemoved.pipe(
      takeUntil(this.terminator),
      filter((x) => x === this.identifier)
    )
  );

  layerProperties = computed(() => ({
    pane: this.pane(),
    geoJson: this.geoJson(),
    interactive: this.interactive(),
    color: this.color(),
    fill: this.fill(),
    fillColor: this.fillColor(),
    fillOpacity: this.fillOpacity(),
    weight: this.weight(),
  }));

  previousLayerProperties = computedPrevious(this.layerProperties);

  #layerAdded = false;
  affectPolygon = explicitEffect([this.layerProperties, this.previousLayerProperties], () => {
    if (!this.#layerAdded) {
      this.addLayer();
      this.#layerAdded = true;
    } else {
      this.editLayer();
    }
  });

  private getLayerPatch() {
    const previous = this.previousLayerProperties();
    const current = this.layerProperties();
    const patch = {} as Partial<typeof current>;
    if (previous) {
      if (previous.pane !== current.pane) {
        patch.pane = current.pane;
      }

      if (previous.geoJson !== current.geoJson) {
        patch.geoJson = current.geoJson;
      }
      if (previous.interactive !== current.interactive) {
        patch.interactive = current.interactive;
      }
      if (previous.color !== current.color) {
        patch.color = current.color;
      }
      if (previous.fill !== current.fill) {
        patch.fill = current.fill;
      }
      if (previous.fillColor !== current.fillColor) {
        patch.fillColor = current.fillColor;
      }
      if (previous.fillOpacity !== current.fillOpacity) {
        patch.fillOpacity = current.fillOpacity;
      }
      if (previous.weight !== current.weight) {
        patch.weight = current.weight;
      }
    } else {
      Object.assign(patch, current);
    }

    return patch;
  }

  addLayer() {
    this.adapter.addLayer(this.identifier, this.layerProperties(), this.radius(), this.fitMap());
  }

  editLayer() {
    this.adapter.editLayer(this.identifier, this.getLayerPatch(), this.radius());
  }

  ngOnDestroy() {
    this.adapter.removeLayer(this.identifier);
  }

  focus() {
    this.adapter.focusLayer(this.identifier);
  }
}
